import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { BellRing, BellOff, Smartphone, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { subscribeToPush, getCurrentPushStatus, isPushSupported } from '../lib/push';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Detect when a driver opened the site in iOS Safari but did NOT install the
// PWA to the home screen — iOS only exposes Web Push to standalone PWAs.
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent || '') && !window.MSStream;
const isStandalonePWA = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  // iOS legacy flag
  // eslint-disable-next-line no-undef
  window.navigator.standalone === true;

/**
 * PushEnableBanner — a high-visibility card that appears on the Driver
 * Dashboard whenever the driver is NOT yet subscribed to OS-level push.
 *
 * The existing tiny pill in the header is easy to ignore. This is the
 * loud, can't-miss version with a "Send test alert" button so drivers
 * can confirm push works end-to-end before they hit the road.
 */
const PushEnableBanner = ({ authHeaders }) => {
  const { toast } = useToast();
  const [status, setStatus] = useState('loading');
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!isPushSupported()) {
      setStatus('unsupported');
      return;
    }
    setStatus(await getCurrentPushStatus());
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const enable = async () => {
    setBusy(true);
    try {
      const res = await subscribeToPush('driver', authHeaders);
      if (res.ok) {
        toast({ title: 'Notifications enabled', description: 'You\'ll now receive alerts even when the screen is locked.' });
      } else if (res.reason === 'denied') {
        toast({
          title: 'Notifications blocked',
          description: 'Open your browser site settings, allow notifications, then reload.',
        });
      } else {
        toast({ title: 'Could not enable notifications' });
      }
    } catch (err) {
      toast({ title: 'Could not enable notifications', description: err?.message || '' });
    } finally {
      setBusy(false);
      await refresh();
    }
  };

  const sendTest = async () => {
    setTestBusy(true);
    try {
      await axios.post(`${API}/driver/push/test`, {}, { headers: authHeaders });
      toast({
        title: 'Test alert sent',
        description: 'Lock your screen now — you should see a notification appear within ~5 seconds.',
      });
    } catch (err) {
      toast({ title: 'Test failed', description: err?.response?.data?.detail || err?.message || '' });
    } finally {
      setTestBusy(false);
    }
  };

  // Already opted in — render a compact "you're set + test" card so the
  // driver can lock their phone and verify lock-screen delivery end-to-end.
  if (status === 'loading') return null;
  if (status === 'subscribed') {
    const showIOSTips = isIOS() && isStandalonePWA();
    return (
      <div
        className="rounded-2xl border border-emerald-400/20 bg-emerald-500/5 p-3 mb-4"
        data-testid="push-banner-subscribed"
      >
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-300 flex-shrink-0" />
          <div className="flex-1 min-w-0 text-sm text-emerald-100/90">
            Lock-screen alerts are <b>on</b> for this device.
          </div>
          <Button
            onClick={sendTest}
            disabled={testBusy}
            variant="ghost"
            size="sm"
            className="text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10 flex-shrink-0"
            data-testid="push-banner-test-btn"
          >
            {testBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <BellRing className="w-4 h-4 mr-1.5" />}
            Send test
          </Button>
        </div>

        {showIOSTips && (
          <details className="mt-2 pt-2 border-t border-emerald-400/15" data-testid="ios-lockscreen-tips">
            <summary className="text-[11px] text-emerald-200/80 cursor-pointer select-none hover:text-emerald-100 flex items-center gap-1">
              <Smartphone className="w-3 h-3" />
              Not getting alerts when phone is locked? Tap for iPhone checklist
            </summary>
            <ol className="mt-2 ml-1 text-[11px] text-emerald-100/85 space-y-1.5 list-decimal list-inside leading-relaxed">
              <li>
                <b>iOS 16.4 or newer</b> — Settings → General → Software Update. Older iPhones can't deliver web push.
              </li>
              <li>
                <b>App must be opened from the Home Screen icon</b> (MR Driver), not from Safari. Safari sessions never get lock-screen pushes.
              </li>
              <li>
                <b>Notifications allowed for MR Driver</b> — Settings → Notifications → MR Driver → toggle <i>Allow Notifications</i>, <i>Lock Screen</i>, <i>Banners</i>, and <i>Sounds</i> all ON.
              </li>
              <li>
                <b>Focus / Do Not Disturb</b> — Control Center → tap the Focus icon. If a Focus is active, add MR Driver to its allow-list (Settings → Focus → [your focus] → Apps).
              </li>
              <li>
                <b>Low Power Mode</b> throttles background pushes — Settings → Battery → turn OFF Low Power Mode while on shift.
              </li>
              <li>
                <b>Background App Refresh</b> — Settings → General → Background App Refresh → ON.
              </li>
              <li>
                If all the above are correct, force-quit MR Driver (swipe up from app switcher) and re-open it once. Then tap <b>Send test</b> above.
              </li>
            </ol>
          </details>
        )}
      </div>
    );
  }

  // iOS Safari before "Add to Home Screen" — push is impossible until installed.
  const needsIOSInstall = isIOS() && !isStandalonePWA();
  if (needsIOSInstall) {
    return (
      <div
        className="rounded-2xl border border-amber-400/30 bg-amber-500/10 p-5 mb-4 text-amber-100"
        data-testid="push-banner-ios-install"
      >
        <div className="flex items-start gap-3">
          <Smartphone className="w-6 h-6 text-amber-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg text-white mb-1">Install the Driver app to get lock-screen alerts</div>
            <p className="text-sm text-amber-100/85 mb-3">
              On iPhone, dispatch alerts only reach you after you install this <b>driver portal</b> to your Home Screen. Follow the steps carefully — iOS sometimes adds the wrong page.
            </p>
            <div className="bg-amber-500/10 border border-amber-400/30 rounded-lg p-3 mb-3 text-xs text-amber-50/90 space-y-1">
              <div className="font-semibold text-amber-200">⚠️ Already added the wrong icon?</div>
              <div>
                If your Home Screen shows the storefront ("MR E-Store") instead of "MR Driver", press &amp; hold the icon and tap <b>Delete Bookmark</b>. Then follow the steps below from this page.
              </div>
            </div>
            <ol className="text-xs text-amber-100/85 space-y-1.5 list-decimal list-inside">
              <li>
                Make sure the Safari URL bar shows a path ending in <code className="text-amber-200 bg-black/30 px-1 rounded">/driver/login</code> or <code className="text-amber-200 bg-black/30 px-1 rounded">/driver</code>.
              </li>
              <li>
                Pull down to refresh the page once. The tab title should now read <b>"MR Driver"</b>, not "Mohammed Riaz E-Store".
              </li>
              <li>Tap the <b>Share</b> button (square with arrow) at the bottom of Safari.</li>
              <li>
                Scroll and tap <b>Add to Home Screen</b>. Confirm the suggested name is <b>"MR Driver"</b> — if it shows the storefront name, tap Cancel and reload this page first.
              </li>
              <li>Tap <b>Add</b> (top-right). Open the new <b>MR Driver</b> icon from your Home Screen.</li>
              <li>Sign in again and tap <b>Enable alerts</b> here.</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'unsupported') {
    return (
      <div className="rounded-2xl border border-red-400/30 bg-red-500/10 p-5 mb-4 text-red-100" data-testid="push-banner-unsupported">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-red-300 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-display text-lg text-white">This browser can't deliver lock-screen alerts</div>
            <p className="text-sm text-red-100/80 mt-1">
              Use Chrome (Android) or Safari (iOS, installed from Home Screen) so dispatch can reach you when your phone is locked.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <div className="rounded-2xl border border-red-400/30 bg-red-500/10 p-5 mb-4 text-red-100" data-testid="push-banner-denied">
        <div className="flex items-start gap-3">
          <BellOff className="w-6 h-6 text-red-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg text-white">Notifications are blocked</div>
            <p className="text-sm text-red-100/80 mt-1">
              Without permission you will <b>not</b> get dispatch alerts when your phone screen is off. Open your browser site settings, allow notifications for this site, then reload this page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // status === 'default' or 'granted-no-sub' — the big enable CTA
  return (
    <div
      className="rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/15 to-emerald-600/5 p-5 mb-4 shadow-lg shadow-emerald-500/10"
      data-testid="push-banner-enable"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
          <BellRing className="w-5 h-5 text-emerald-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg text-white">Turn on lock-screen alerts</div>
          <p className="text-sm text-slate-300 mt-1">
            New deliveries, broadcasts and chat messages will buzz your phone even when the screen is locked or the app is in the background.
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Button
              onClick={enable}
              disabled={busy}
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full px-5"
              data-testid="push-banner-enable-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <BellRing className="w-4 h-4 mr-1.5" />}
              Enable alerts
            </Button>
            {status === 'granted-no-sub' && (
              <span className="text-xs text-amber-300 inline-flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Permission granted but no subscription yet — tap above.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushEnableBanner;
